import PropTypes from 'prop-types';
import { get, round } from 'lodash-es';
import { EdmundsAPI } from 'client/data/api/api-client';
import { withMetrics } from 'client/data/api/api-metrics';
import { createModelSegment } from 'client/data/luckdragon/segment';

import { VisitorModel } from './visitor';

export const MAX_PAGE_NUM = 500;

const Dealer = PropTypes.shape({
  id: PropTypes.number,
  rooftopId: PropTypes.number,
  name: PropTypes.string,
  logicalName: PropTypes.string,
  rooftopLogicalName: PropTypes.string,
  parentDealershipName: PropTypes.string,
  make: PropTypes.string,
  address: PropTypes.shape({
    city: PropTypes.string,
    stateCode: PropTypes.string,
    stateName: PropTypes.string,
    street: PropTypes.string,
    zip: PropTypes.string,
    county: PropTypes.string,
    country: PropTypes.string,
  }),
  phones: PropTypes.arrayOf(PropTypes.string),
  location: PropTypes.shape({
    lat: PropTypes.number,
    lon: PropTypes.number,
  }),
  distance: PropTypes.number,
  productFeatures: PropTypes.shape({
    active: PropTypes.bool,
    directDealer: PropTypes.bool,
    types: PropTypes.arrayOf(PropTypes.string),
    productLineItems: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        group: PropTypes.string,
        product: PropTypes.string,
      })
    ),
  }),
  phoneNumbers: PropTypes.shape({}),
  dealerType: PropTypes.string,
  sortOrders: PropTypes.shape({
    tier: PropTypes.number,
  }),
  publishDate: PropTypes.string,
  groupId: PropTypes.string,
  groupName: PropTypes.string,
  newUsedType: PropTypes.string,
  showroomPage: PropTypes.string,
  salesReviewCount: PropTypes.number,
  salesRating: PropTypes.number,
});

export const DealershipsEntities = { Dealer };

export const buildDealershipsPath = ({ make = 'all', pageNum = 1, directDealer = false }) =>
  `makes["${make}"].page["${pageNum}"].directDealer["${directDealer}"]`;
export const buildMileOneDealershipPath = ({ make = 'all', pageSize = 1 }) =>
  `makes["${make}"].page["${pageSize}"].mileOne`;
export const buildNationalDealershipsPath = ({ pageNum = 1 }) => `national.page["${pageNum}"]`;

export const buildClosestDealershipsPath = (mobile, { make }) => {
  const platform = mobile ? 'mobile' : 'wired';

  return `closestDealers.platform["${platform}"].makes["${make}"]`;
};

export const buildClosestDealersPath = ({ make }) => `closestDealers.makes["${make}"]`;

export const getNearestDeliveryStorePath = () => 'nearestDeliveryStore';

export const PAGE_SIZE = 18;

export const DealershipsModel = createModelSegment('dealerships', [
  /**
   * Returns national dealerships
   * @see buildNationalDealershipsPath
   */
  {
    path: 'national.page["{pageNum}"]',
    async resolve(match, context) {
      const { pageNum } = match;
      const { latitude, longitude } = await context.resolveValue('location', VisitorModel);

      const url = `/dealer/v5/find?dealerType=DEALERFRANCHISE&productFeatures.active=true&view=DEFAULT&pageSize=${PAGE_SIZE}&pageNum=${
        pageNum > MAX_PAGE_NUM ? MAX_PAGE_NUM : pageNum
      }&groupby=rooftopId&sortBy=displayInfo.salesOverallRating:desc,displayInfo.salesReviewCount:desc&lat=${latitude}&lon=${longitude}`;

      return withMetrics(EdmundsAPI, context)
        .fetchJson(url)
        .then(data => ({ ...data, totalPages: data.totalPages > MAX_PAGE_NUM ? MAX_PAGE_NUM : data.totalPages }))
        .catch(() => ({ totalPages: 0, results: [] }));
    },
  },
  /**
   * Returns dealerships by make
   * Example: https://qa-21-www.edmunds.com/gateway/api/dealer/v5/find?dealerType=DEALERFRANCHISE&productFeatures.active=true&view=DEFAULT&pageSize=18&pageNum=1&make=chrysler&address.zip=58067&radius=500&sortBy=_geo:asc
   *
   * If make === 'all' then adds 'groupby=rooftopId' to exclude duplicates
   * Example: https://qa-21-www.edmunds.com/gateway/api/dealer/v5/find?dealerType=DEALERFRANCHISE&productFeatures.active=true&view=DEFAULT&pageSize=18&pageNum=1&address.zip=58067&radius=500&sortBy=_geo:asc&groupby=rooftopId
   *
   * @see buildDealershipsPath
   */
  {
    path: 'makes["{make}"].page["{pageNum}"].directDealer["{directDealer}"]',
    async resolve(match, context) {
      const { make, pageNum, directDealer } = match;
      const { zipCode } = await context.resolveValue('location', VisitorModel);

      const url = `/dealer/v5/find?dealerType=DEALERFRANCHISE&productFeatures.active=true${
        directDealer === 'true' ? '&productFeatures.directDealer=true' : ''
      }&view=DEFAULT&pageSize=${PAGE_SIZE}&pageNum=${pageNum > MAX_PAGE_NUM ? MAX_PAGE_NUM : pageNum}${
        make === 'all' ? '&groupby=rooftopId' : `&make=${make}`
      }${zipCode ? `&address.zip=${zipCode}&radius=500&sortBy=_geo:asc,displayInfo.salesOverallRating:desc` : ''}`;

      return withMetrics(EdmundsAPI, context)
        .fetchJson(url)
        .then(data => ({ ...data, totalPages: data.totalPages > MAX_PAGE_NUM ? MAX_PAGE_NUM : data.totalPages }))
        .catch(() => ({ totalPages: 0, results: [] }));
    },
  },
  {
    path: 'makes["{make}"].page["{pageSize}"].mileOne',
    async resolve(match, context) {
      const { make, pageSize } = match;
      const { zipCode } = await context.resolveValue('location', VisitorModel);

      const url = `/dealer/v5/find?dealerType=DEALERFRANCHISE&productFeatures.active=true&productFeatures.directDealer=true&view=DEFAULT&pageSize=${pageSize}&pageNum=1${
        make === 'all' ? '' : `&make=${make}`
      }&groupby=rooftopId&address.zip=${zipCode}&radius=50&displayInfo.parentDealershipName=MileOne Automotive&sortBy=displayInfo.parentDealershipName,_geo`;

      return withMetrics(EdmundsAPI, context)
        .fetchJson(url)
        .then(data => ({ ...get(data, 'results[0]', {}) }))
        .catch(() => ({}));
    },
  },

  {
    path: 'closestDealers.platform["{platform}"].makes["{make}"]',
    async resolve(match, context) {
      try {
        const { make, platform } = match;
        const { zipCode, latitude, longitude } = await context.resolveValue('location', VisitorModel);
        const isMobile = platform === 'mobile';

        const url = `/dealer/v5/find?dealerType=DEALERFRANCHISE&productFeatures.active=true&view=DEFAULT&pageSize=10&pageNum=1&make=${make}$&address.zip=${zipCode}&lat=${latitude}&lon=${longitude}&radius=500&sortBy=_geo:asc`;

        const data = await withMetrics(EdmundsAPI, context).fetchJson(url);
        const dealers = get(data, 'results', []).map(d => ({
          ...d,
          distanceLabel: isMobile
            ? `${d.name} (${round(d.distance, 1)} mi)`
            : `${d.name} - ${d.address.city}, ${d.address.stateCode} (${round(d.distance, 1)} mi)`,
        }));
        return dealers;
      } catch {
        return [];
      }
    },
  },

  {
    path: 'closestDealers.makes["{make}"]',
    async resolve(match, context) {
      try {
        const { make } = match;
        const { zipCode, latitude, longitude } = await context.resolveValue('location', VisitorModel);

        const url = `/dealer/v5/find?dealerType=DEALERFRANCHISE&productFeatures.active=true&view=DEFAULT&pageSize=10&pageNum=1&make=${make}$&address.zip=${zipCode}&lat=${latitude}&lon=${longitude}&radius=500&sortBy=_geo:asc`;

        const data = await withMetrics(EdmundsAPI, context).fetchJson(url);
        const dealers = get(data, 'results', []).map(d => ({
          ...d,
          distanceLabel: `${d.name} (${round(d.distance, 1)} mi)`,
        }));

        return dealers;
      } catch {
        return [];
      }
    },
  },
  /**
   * @see getNearestDeliveryStorePath
   */
  {
    path: 'nearestDeliveryStore',
    async resolve(match, context) {
      const { zipCode, latitude, longitude } = await context.resolveValue('location', VisitorModel);

      const url = `/dealer/v5/nearestDeliveryStore?zip=${zipCode}&lt=${latitude}&long=${longitude}&parentDealershipName=CarMax%20Inc.&radius=50&pageNum=1&pageSize=1&sortBy=distance:ASC`;

      return withMetrics(EdmundsAPI, context)
        .fetchJson(url)
        .then(response => response?.nearestFranchises?.[0]?.franchiseId)
        .catch(() => null);
    },
  },
]);
